import React, { useState, useEffect } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { BASE_URL } from "@/config/api.config";

function ImageComponent({
  imageUrl,
  apiBaseUrl = "http://localhost:5000",
  alt = "Image content",
  className = "",
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [finalUrl, setFinalUrl] = useState(null);

  useEffect(() => {
    if (!imageUrl) {
      setLoading(false);
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);

    // Build the correct URL
    let correctUrl;
    if (imageUrl.startsWith("http")) {
      correctUrl = imageUrl;
    } else {
      const cleanUrl = imageUrl.replace(/^\/+/, "");
      correctUrl = `${BASE_URL}/${cleanUrl}`;
    }

    // First, try to fetch the image to check CORS
    fetch(correctUrl, {
      method: "HEAD",
      mode: "cors",
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          setFinalUrl(correctUrl);
          setLoading(false);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      })
      .catch((fetchError) => {
        // Fallback to direct image loading
        const img = new Image();
        img.crossOrigin = "use-credentials";

        img.onload = () => {
          setFinalUrl(correctUrl);
          setLoading(false);
        };

        img.onerror = (imgError) => {
          setError(true);
          setLoading(false);
        };

        img.src = correctUrl;
      });
  }, [imageUrl, apiBaseUrl]);

  const handleRetry = () => {
    setLoading(true);
    setError(false);

    // Force re-run the effect by creating a new timestamp
    const timestamp = Date.now();
    const retryUrl = finalUrl ? `${finalUrl}?t=${timestamp}` : null;

    if (retryUrl) {
      const img = new Image();
      img.crossOrigin = "use-credentials";

      img.onload = () => {
        setFinalUrl(retryUrl);
        setLoading(false);
      };

      img.onerror = () => {
        setError(true);
        setLoading(false);
      };

      img.src = retryUrl;
    }
  };

  if (!imageUrl) {
    return (
      <div className="flex flex-col items-center justify-center text-gray-400 h-48 w-full">
        <div className="h-12 w-12 bg-gray-300 rounded flex items-center justify-center">
          <span className="text-gray-500 text-xl">📷</span>
        </div>
        <p className="text-sm mt-2">No image available</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center bg-gray-100 h-48 w-full">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        <p className="text-sm text-gray-500 mt-2">Loading image...</p>
      </div>
    );
  }

  if (error) {
    const displayUrl = imageUrl.startsWith("http")
      ? imageUrl
      : `${apiBaseUrl}/${imageUrl.replace(/^\/+/, "")}`;

    return (
      <div className="flex flex-col items-center justify-center bg-gray-100 h-48 w-full p-4">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <p className="text-sm text-red-500 mt-1">Failed to load image</p>
        <p className="text-xs text-red-400 mt-1">Possible CORS issue</p>

        <button
          onClick={handleRetry}
          className="mt-3 px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" /> Retry
        </button>

        <div className="mt-3 text-center">
          <p className="text-xs text-gray-600">Trying to load:</p>
          <div className="text-xs text-gray-700 break-all mt-1 p-2 bg-gray-200 rounded max-w-sm">
            {displayUrl}
          </div>
        </div>

        <div className="mt-2 text-center">
          <p
            className="text-xs text-blue-600 cursor-pointer hover:underline"
            onClick={() => window.open(displayUrl, "_blank")}
          >
            Open in new tab to test
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex items-center justify-center bg-gray-50">
      <img
        src={finalUrl}
        alt={alt}
        className={`max-h-full max-w-full object-contain ${className}`}
        crossOrigin="use-credentials"
        onError={() => {
          setError(true);
        }}
      />
    </div>
  );
}

export default ImageComponent;
